import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import kioskClient, { kioskLogout } from '../api/KioskClient';
import './KioskView.css';

const STATUS_ORDER = ['queued', 'in_progress', 'on_hold', 'complete'];

const STATUS_LABELS = {
  queued:      'Queued',
  in_progress: 'In Progress',
  on_hold:     'On Hold',
  complete:    'Complete',
};

const STATUS_COLORS = {
  queued:      { bg: 'rgba(107,114,128,0.12)', color: '#9ca3af' },
  in_progress: { bg: 'rgba(79,142,247,0.12)',  color: '#4f8ef7' },
  on_hold:     { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
  complete:    { bg: 'rgba(34,197,94,0.12)',   color: '#22c55e' },
};

export default function KioskView() {
  const { slug } = useParams();

  // QR codes encode this page's URL with params:
  //   ?job=1042      -> auto-focus that job after PIN login
  //   ?location=<id> -> filter the board to that location
  const urlParams = new URLSearchParams(window.location.search);
  const scannedJob = (urlParams.get('job') || '').trim();
  const scannedLocation = (urlParams.get('location') || '').trim();

  // Search / filtering
  const [search, setSearch] = useState('');
  const [locFilter, setLocFilter] = useState(scannedLocation || null);

  // Org bootstrap
  const [org, setOrg] = useState(null);
  const [orgError, setOrgError] = useState('');
  const [bootLoading, setBootLoading] = useState(true);

  // PIN entry
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // Session
  const [session, setSession] = useState(null); // { user, settings }
  const [secondsLeft, setSecondsLeft] = useState(null);

  // Jobs
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [moveJob, setMoveJob] = useState(null); // job being moved
  const [toast, setToast] = useState('');

  const timerRef = useRef(null);
  const pollRef = useRef(null);

  const pinLength = org?.kiosk_settings?.pin_length || 4;
  const accent = org?.accentColor || '#3b82f6';

  // ── Bootstrap: resolve org from slug (public route) ─────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `https://api.traceoperations.com/org/by-slug/${slug}`
        );
        setOrg(res.data);
        if (!res.data.kiosk_settings?.enabled) {
          setOrgError('Kiosk mode is disabled for this shop.');
        }
      } catch {
        setOrgError("We couldn't find this shop. Check the kiosk link.");
      } finally {
        setBootLoading(false);
      }
    })();
  }, [slug]);

  // ── Session countdown / auto-logout ─────────────────────────────
  const endSession = useCallback((message) => {
    kioskLogout();
    setSession(null);
    setJobs([]);
    setPin('');
    setSecondsLeft(null);
    if (timerRef.current) clearInterval(timerRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
    if (message) setPinError(message);
  }, []);

  const startCountdown = useCallback((timeoutSec) => {
    setSecondsLeft(timeoutSec);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          endSession('Session timed out. Enter your PIN to continue.');
          return null;
        }
        return s - 1;
      });
    }, 1000);
  }, [endSession]);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  // ── Jobs: load + light polling so the board stays live ──────────
  const loadJobs = useCallback(async () => {
    try {
      const [queueRes, locRes] = await Promise.all([
        kioskClient.get('/jobs/queue?all=1'),
        kioskClient.get('/locations'),
      ]);
      setJobs(queueRes.data);
      setLocations(locRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        endSession('Session expired. Enter your PIN to continue.');
      }
    } finally {
      setJobsLoading(false);
    }
  }, [endSession]);

  useEffect(() => {
    if (!session) return;
    setJobsLoading(true);
    loadJobs();
    pollRef.current = setInterval(loadJobs, 15000);
    return () => clearInterval(pollRef.current);
  }, [session, loadJobs]);

  // ── PIN pad ──────────────────────────────────────────────────────
  const pressDigit = (d) => {
    setPinError('');
    if (pin.length >= pinLength) return;
    const next = pin + d;
    setPin(next);
    if (next.length === pinLength) submitPin(next);
  };

  const submitPin = async (fullPin) => {
    setLoggingIn(true);
    setPinError('');
    try {
      const res = await axios.post(
        'https://api.traceoperations.com/kiosk/pin-login',
        { pin: fullPin, org_id: org.id }
      );
      sessionStorage.setItem('kiosk_token', res.data.token);
      sessionStorage.setItem('kiosk_user', JSON.stringify(res.data.user));
      // QR-scanned job: pre-fill the search so the board opens focused on it.
      if (scannedJob) setSearch(scannedJob);
      setSession({ user: res.data.user, settings: res.data.settings });
      startCountdown(res.data.settings.timeout_sec || 120);
      setPin('');
    } catch (err) {
      setPin('');
      setPinError(err.response?.data?.error || 'PIN login failed. Try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  // ── Job actions (respect kiosk permissions) ─────────────────────
  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  const advanceStatus = async (job) => {
    if (!session?.settings?.allow_update_status) return;
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(job.status) + 1) % STATUS_ORDER.length];
    // Optimistic update; reconcile on next poll.
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, status: next } : j)));
    try {
      await kioskClient.put(`/jobs/${job.id}`, { status: next });
      flash(`#${job.job_number} → ${STATUS_LABELS[next]}`);
    } catch (err) {
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, status: job.status } : j)));
      if (err.response?.status === 401) endSession('Session expired. Enter your PIN to continue.');
      else flash('Update failed — try again');
    }
  };

  const moveToLocation = async (job, locationId) => {
    setMoveJob(null);
    const loc = locations.find((l) => String(l.id) === String(locationId));
    try {
      await kioskClient.put(`/jobs/${job.id}`, { location_id: locationId });
      setJobs((prev) =>
        prev.map((j) => (j.id === job.id ? { ...j, location_id: locationId, location_name: loc?.name } : j))
      );
      flash(`#${job.job_number} → ${loc?.name || 'moved'}`);
    } catch (err) {
      if (err.response?.status === 401) endSession('Session expired. Enter your PIN to continue.');
      else flash('Move failed — try again');
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  if (bootLoading) {
    return <div className="kv-root kv-center">Loading…</div>;
  }

  if (orgError) {
    return (
      <div className="kv-root kv-center">
        <div className="kv-logo">TRACE<span>OPS</span></div>
        <div className="kv-error-big">{orgError}</div>
      </div>
    );
  }

  // PIN screen
  if (!session) {
    return (
      <div className="kv-root kv-center" style={{ '--kv-accent': accent }}>
        <div className="kv-logo">TRACE<span>OPS</span></div>
        <div className="kv-shop-name">{org.name}</div>
        {org.kiosk_settings?.welcome_message && (
          <div className="kv-welcome">{org.kiosk_settings.welcome_message}</div>
        )}

        <div className="kv-pin-dots">
          {Array.from({ length: pinLength }).map((_, i) => (
            <span key={i} className={`kv-pin-dot ${i < pin.length ? 'filled' : ''}`} />
          ))}
        </div>

        {pinError && <div className="kv-pin-error">{pinError}</div>}

        <div className={`kv-pad ${loggingIn ? 'kv-pad-disabled' : ''}`}>
          {[1,2,3,4,5,6,7,8,9].map((d) => (
            <button key={d} className="kv-key" onClick={() => pressDigit(String(d))}>{d}</button>
          ))}
          <span />
          <button className="kv-key" onClick={() => pressDigit('0')}>0</button>
          <button className="kv-key kv-key-back" onClick={() => setPin(pin.slice(0, -1))}>⌫</button>
        </div>
      </div>
    );
  }

  // Operator board.
  // Complete jobs are hidden from the default board, but a SCAN or SEARCH
  // must always resolve -- a driver standing at a finished pallet should see
  // "Complete", never "no match".
  const q = search.trim().toLowerCase();
  const visibleJobs = jobs.filter((j) => {
    if (j.status === 'complete' && !q) return false;
    if (locFilter && String(j.location_id) !== String(locFilter)) return false;
    if (q) {
      const hay = `${j.job_number} ${j.description || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const filteredLocName = locFilter
    ? (locations.find((l) => String(l.id) === String(locFilter))?.name || 'location')
    : null;

  return (
    <div className="kv-root" style={{ '--kv-accent': accent }}>
      <header className="kv-header">
        <div className="kv-logo kv-logo-sm">TRACE<span>OPS</span></div>
        <div className="kv-header-user">
          <span className="kv-user-name">{session.user.name}</span>
          {secondsLeft != null && (
            <span className={`kv-timer ${secondsLeft <= 15 ? 'urgent' : ''}`}>
              {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
            </span>
          )}
          <button className="kv-done-btn" onClick={() => endSession()}>Done</button>
        </div>
      </header>

      {toast && <div className="kv-toast">{toast}</div>}

      <div className="kv-searchbar">
        <input
          className="kv-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search job # or description…"
          inputMode="search"
        />
        {search && (
          <button className="kv-search-clear" onClick={() => setSearch('')}>✕</button>
        )}
        {filteredLocName && (
          <button className="kv-loc-chip" onClick={() => setLocFilter(null)}>
            📍 {filteredLocName} ✕
          </button>
        )}
      </div>

      <main className="kv-board">
        {jobsLoading && visibleJobs.length === 0 && (
          <div className="kv-empty">Loading jobs…</div>
        )}
        {!jobsLoading && visibleJobs.length === 0 && (
          <div className="kv-empty">
            {q || locFilter ? `No jobs match${q ? ` "${search}"` : ''}.` : 'No active jobs on the floor. 🎉'}
          </div>
        )}

        {visibleJobs.map((job) => {
          const sc = STATUS_COLORS[job.status] || STATUS_COLORS.queued;
          return (
            <div key={job.id} className="kv-job">
              <div className="kv-job-top">
                <span className="kv-job-number">#{job.job_number}</span>
                <span className="kv-job-status" style={{ background: sc.bg, color: sc.color }}>
                  {STATUS_LABELS[job.status] || job.status}
                </span>
              </div>
              <div className="kv-job-desc">{job.description || '—'}</div>
              <div className="kv-job-meta">
                <span>📍 {job.location_name || 'No location'}</span>
                {job.department_name && <span>· {job.department_name}</span>}
                {job.quantity != null && <span>· qty {job.quantity}</span>}
              </div>

              <div className="kv-job-actions">
                {session.settings.allow_update_status && (
                  <button className="kv-action" onClick={() => advanceStatus(job)}>
                    Advance status →
                  </button>
                )}
                {session.settings.allow_move_jobs && (
                  <button className="kv-action kv-action-secondary" onClick={() => setMoveJob(job)}>
                    Move location
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </main>

      {moveJob && (
        <div className="kv-modal-overlay" onClick={() => setMoveJob(null)}>
          <div className="kv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kv-modal-title">Move #{moveJob.job_number} to…</div>
            <div className="kv-modal-locations">
              {locations.map((l) => (
                <button
                  key={l.id}
                  className={`kv-loc-btn ${String(l.id) === String(moveJob.location_id) ? 'current' : ''}`}
                  onClick={() => moveToLocation(moveJob, l.id)}
                  disabled={String(l.id) === String(moveJob.location_id)}
                >
                  {l.name}
                </button>
              ))}
            </div>
            <button className="kv-modal-cancel" onClick={() => setMoveJob(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}